import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import type { CompanyFundingScenario } from "../../domain/companyFunding";
import { validateCompanyFundingScenario } from "../../engine/companyFundingValidation";
import { canonicalFundingJson } from "../../engine/fundingProvenance";
const fundingWrites = new WeakMap<DbExecutor, Promise<unknown>>();
import {
  DEFAULT_PLAN_SETTINGS,
  type CapitalSource,
  type PlanOverride,
  type PlanSettings,
  type ScenarioProject,
} from "../../domain/financing";

/**
 * BUSINESS-LEVEL PLANNING persistence (spec §73N/P/R).
 *
 * Workspace-wide, deliberately owned by no project: the capital the
 * business has, the scenario fixtures being weighed, the plan's own
 * settings, and planning start overrides. A scenario is NOT a project and
 * a plan override never mutates a project's committed schedule.
 */

interface CapitalRow {
  id: string;
  name: string;
  kind: string;
  amount_cents: number | null;
  loc_limit_cents: number | null;
  loc_balance_cents: number | null;
  apr_pct_x100: number | null;
  notes: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

const n = (v: number | null): number | null => (v === null ? null : Number(v));

function mapCapital(row: CapitalRow): CapitalSource {
  return {
    id: row.id,
    name: row.name,
    kind: row.kind as CapitalSource["kind"],
    amountCents: n(row.amount_cents),
    locLimitCents: n(row.loc_limit_cents),
    locBalanceCents: n(row.loc_balance_cents),
    aprPctX100: n(row.apr_pct_x100),
    notes: row.notes,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

interface ScenarioRow {
  id: string;
  name: string;
  strategy: string;
  source_project_id: string | null;
  purchase_price_cents: number | null;
  rehab_cents: number | null;
  duration_days: number | null;
  down_payment_cents: number | null;
  financing_commitment_cents: number | null;
  interest_rate_pct_x100: number | null;
  origination_fee_cents: number | null;
  expected_sale_cents: number | null;
  selling_costs_pct_x100: number | null;
  monthly_rent_cents: number | null;
  hold_days_after_rehab: number | null;
  planned_start_date: string | null;
  notes: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapScenario(row: ScenarioRow): ScenarioProject {
  return {
    id: row.id,
    name: row.name,
    strategy: row.strategy as ScenarioProject["strategy"],
    sourceProjectId: row.source_project_id,
    purchasePriceCents: n(row.purchase_price_cents),
    rehabCents: n(row.rehab_cents),
    durationDays: n(row.duration_days),
    downPaymentCents: n(row.down_payment_cents),
    financingCommitmentCents: n(row.financing_commitment_cents),
    interestRatePctX100: n(row.interest_rate_pct_x100),
    originationFeeCents: n(row.origination_fee_cents),
    expectedSaleCents: n(row.expected_sale_cents),
    sellingCostsPctX100: n(row.selling_costs_pct_x100),
    monthlyRentCents: n(row.monthly_rent_cents),
    holdDaysAfterRehab: n(row.hold_days_after_rehab),
    plannedStartDate: row.planned_start_date,
    notes: row.notes,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export type ScenarioFields = Omit<ScenarioProject, "id" | "archivedAt" | "createdAt" | "updatedAt">;
export type CapitalFields = Omit<CapitalSource, "id" | "archivedAt" | "createdAt" | "updatedAt">;

export class CapitalRepository {
  constructor(private readonly db: DbExecutor) {}

  /* ---------------- capital sources ---------------- */

  async listCapitalSources(): Promise<CapitalSource[]> {
    const rows = await this.db.select<CapitalRow>(
      "SELECT * FROM capital_sources WHERE archived_at IS NULL ORDER BY created_at",
    );
    return rows.map(mapCapital);
  }

  async createCapitalSource(fields: CapitalFields): Promise<CapitalSource> {
    const id = generateId();
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT INTO capital_sources (id, name, kind, amount_cents, loc_limit_cents, loc_balance_cents,
         apr_pct_x100, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, fields.name, fields.kind, fields.amountCents, fields.locLimitCents, fields.locBalanceCents,
       fields.aprPctX100, fields.notes, now, now],
    );
    const rows = await this.db.select<CapitalRow>("SELECT * FROM capital_sources WHERE id = ?", [id]);
    return mapCapital(rows[0]);
  }

  async updateCapitalSource(id: string, fields: CapitalFields): Promise<CapitalSource> {
    await this.db.execute(
      `UPDATE capital_sources SET name = ?, kind = ?, amount_cents = ?, loc_limit_cents = ?,
         loc_balance_cents = ?, apr_pct_x100 = ?, notes = ?, updated_at = ? WHERE id = ?`,
      [fields.name, fields.kind, fields.amountCents, fields.locLimitCents, fields.locBalanceCents,
       fields.aprPctX100, fields.notes, new Date().toISOString(), id],
    );
    const rows = await this.db.select<CapitalRow>("SELECT * FROM capital_sources WHERE id = ?", [id]);
    return mapCapital(rows[0]);
  }

  async archiveCapitalSource(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute("UPDATE capital_sources SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
  }

  /* ---------------- scenario projects ---------------- */

  async listScenarios(): Promise<ScenarioProject[]> {
    const rows = await this.db.select<ScenarioRow>(
      "SELECT * FROM scenario_projects WHERE archived_at IS NULL ORDER BY created_at",
    );
    return rows.map(mapScenario);
  }

  async getScenario(id: string): Promise<ScenarioProject | null> {
    const rows = await this.db.select<ScenarioRow>("SELECT * FROM scenario_projects WHERE id = ?", [id]);
    return rows.length === 0 ? null : mapScenario(rows[0]);
  }

  async createScenario(fields: ScenarioFields): Promise<ScenarioProject> {
    const id = generateId();
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT INTO scenario_projects (id, name, strategy, source_project_id, purchase_price_cents, rehab_cents,
         duration_days, down_payment_cents, financing_commitment_cents, interest_rate_pct_x100,
         origination_fee_cents, expected_sale_cents, selling_costs_pct_x100, monthly_rent_cents,
         hold_days_after_rehab, planned_start_date, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, fields.name, fields.strategy, fields.sourceProjectId, fields.purchasePriceCents, fields.rehabCents,
       fields.durationDays, fields.downPaymentCents, fields.financingCommitmentCents, fields.interestRatePctX100,
       fields.originationFeeCents, fields.expectedSaleCents, fields.sellingCostsPctX100, fields.monthlyRentCents,
       fields.holdDaysAfterRehab, fields.plannedStartDate, fields.notes, now, now],
    );
    return (await this.getScenario(id))!;
  }

  async updateScenario(id: string, fields: ScenarioFields): Promise<ScenarioProject> {
    await this.db.execute(
      `UPDATE scenario_projects SET name = ?, strategy = ?, source_project_id = ?, purchase_price_cents = ?,
         rehab_cents = ?, duration_days = ?, down_payment_cents = ?, financing_commitment_cents = ?,
         interest_rate_pct_x100 = ?, origination_fee_cents = ?, expected_sale_cents = ?,
         selling_costs_pct_x100 = ?, monthly_rent_cents = ?, hold_days_after_rehab = ?,
         planned_start_date = ?, notes = ?, updated_at = ? WHERE id = ?`,
      [fields.name, fields.strategy, fields.sourceProjectId, fields.purchasePriceCents, fields.rehabCents,
       fields.durationDays, fields.downPaymentCents, fields.financingCommitmentCents, fields.interestRatePctX100,
       fields.originationFeeCents, fields.expectedSaleCents, fields.sellingCostsPctX100, fields.monthlyRentCents,
       fields.holdDaysAfterRehab, fields.plannedStartDate, fields.notes, new Date().toISOString(), id],
    );
    return (await this.getScenario(id))!;
  }

  async archiveScenario(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute("UPDATE scenario_projects SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
  }

  /* ---------------- plan settings ---------------- */

  async getPlanSettings(): Promise<PlanSettings> {
    const rows = await this.db.select<{
      min_reserve_cents: number; horizon_months: number; objective: string;
      conservative_overrun_pct_x100: number; conservative_duration_pct_x100: number;
      conservative_draw_delay_days: number; conservative_sale_delay_days: number;
    }>("SELECT * FROM plan_settings LIMIT 1");
    if (rows.length === 0) return { ...DEFAULT_PLAN_SETTINGS };
    const r = rows[0];
    return {
      minReserveCents: Number(r.min_reserve_cents),
      horizonMonths: Number(r.horizon_months),
      objective: r.objective as PlanSettings["objective"],
      conservativeOverrunPctX100: Number(r.conservative_overrun_pct_x100),
      conservativeDurationPctX100: Number(r.conservative_duration_pct_x100),
      conservativeDrawDelayDays: Number(r.conservative_draw_delay_days),
      conservativeSaleDelayDays: Number(r.conservative_sale_delay_days),
    };
  }

  async savePlanSettings(settings: PlanSettings): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ id: string }>("SELECT id FROM plan_settings LIMIT 1");
    const params = [
      settings.minReserveCents, settings.horizonMonths, settings.objective,
      settings.conservativeOverrunPctX100, settings.conservativeDurationPctX100,
      settings.conservativeDrawDelayDays, settings.conservativeSaleDelayDays, now,
    ];
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO plan_settings (min_reserve_cents, horizon_months, objective,
           conservative_overrun_pct_x100, conservative_duration_pct_x100,
           conservative_draw_delay_days, conservative_sale_delay_days, updated_at, id)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [...params, generateId()],
      );
    } else {
      await this.db.execute(
        `UPDATE plan_settings SET min_reserve_cents = ?, horizon_months = ?, objective = ?,
           conservative_overrun_pct_x100 = ?, conservative_duration_pct_x100 = ?,
           conservative_draw_delay_days = ?, conservative_sale_delay_days = ?, updated_at = ?
         WHERE id = ?`,
        [...params, existing[0].id],
      );
    }
  }

  /* ---------------- plan start overrides ---------------- */

  /** Assumptions only. Optimistic revision checks prevent overwriting another open plan. */
  async listFundingScenarios(): Promise<Array<{ scenario: CompanyFundingScenario; revision: number }>> {
    const rows = await this.db.select<{payload_json:string;revision:number}>(
      "SELECT payload_json, revision FROM company_funding_scenarios ORDER BY updated_at DESC, id",
    );
    return rows.map(row => {
      const scenario = JSON.parse(row.payload_json) as CompanyFundingScenario;
      if (!validateCompanyFundingScenario(scenario).valid) throw new Error("Saved funding assumptions need review; no financial record was changed.");
      return {scenario, revision:Number(row.revision)};
    });
  }

  async saveFundingScenario(scenario: CompanyFundingScenario, expectedRevision: number): Promise<number> {
    const payload = JSON.stringify(scenario);
    if (payload.length > 250_000 || !validateCompanyFundingScenario(scenario).valid) throw new Error("Review the funding assumptions before saving.");
    if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) throw new Error("Invalid planning revision.");
    const prior = fundingWrites.get(this.db) ?? Promise.resolve();
    const write = prior.catch(() => undefined).then(() => this.db.transaction(async () => {
      const rows = await this.db.select<{revision:number}>("SELECT revision FROM company_funding_scenarios WHERE id = ?",[scenario.id]);
      if ((rows[0]?.revision ?? 0) !== expectedRevision) throw new Error("This plan changed elsewhere. Reload before saving.");
      const now = new Date().toISOString(), revision = expectedRevision + 1;
      if (rows.length === 0) await this.db.execute(
        "INSERT INTO company_funding_scenarios (id,payload_json,revision,created_at,updated_at) VALUES (?,?,?,?,?)",
        [scenario.id,payload,revision,now,now],
      );
      else await this.db.execute("UPDATE company_funding_scenarios SET payload_json = ?, revision = ?, updated_at = ? WHERE id = ?",[payload,revision,now,scenario.id]);
      return revision;
    }));
    fundingWrites.set(this.db,write);
    return write;
  }

  async listFundingRuns(scenarioId:string):Promise<Array<{id:string;scenarioRevision:number;fingerprint:string;engineVersion:string;variant:string;createdAt:string}>>{
    if(typeof scenarioId!=="string"||!scenarioId||scenarioId.length>500)throw new Error("Choose a saved planning scenario.");
    return this.db.select("SELECT id,scenario_revision AS scenarioRevision,fingerprint,engine_version AS engineVersion,variant,created_at AS createdAt FROM company_funding_runs WHERE scenario_id=? ORDER BY created_at DESC,id LIMIT 100",[scenarioId]);
  }
  async recordFundingRun(scenario:CompanyFundingScenario,expectedRevision:number,record:{fingerprint:string;engineVersion:string;variant:"expected"|"conservative";source:unknown;result:unknown}):Promise<{id:string;fingerprint:string}>{
    if(!/^[a-f0-9]{64}$/.test(record.fingerprint)||record.engineVersion.length>100||!Number.isSafeInteger(expectedRevision)||expectedRevision<1)throw new Error("Save and review a valid scenario before freezing a comparison.");
    const request=canonicalFundingJson(scenario),source=canonicalFundingJson(record.source),result=canonicalFundingJson(record.result);
    if(request.length>250000||source.length>4_000_000||result.length>4_000_000)throw new Error("Comparison exceeds the supported evidence bound; narrow the planning horizon.");
    const prior=fundingWrites.get(this.db)??Promise.resolve();
    const write=prior.catch(()=>undefined).then(()=>this.db.transaction(async()=>{
      const saved=(await this.db.select<{payload_json:string;revision:number}>("SELECT payload_json,revision FROM company_funding_scenarios WHERE id=?",[scenario.id]))[0];
      if(!saved||saved.revision!==expectedRevision||canonicalFundingJson(JSON.parse(saved.payload_json))!==request)throw new Error("Save current assumptions before freezing this comparison; another edit may have changed the scenario.");
      const previous=(await this.db.select<{id:string;fingerprint:string}>("SELECT id,fingerprint FROM company_funding_runs WHERE scenario_id=? AND scenario_revision=? AND fingerprint=? AND variant=? LIMIT 1",[scenario.id,expectedRevision,record.fingerprint,record.variant]))[0];
      if(previous)return previous;
      const id=generateId();await this.db.execute("INSERT INTO company_funding_runs (id,scenario_id,scenario_revision,fingerprint,engine_version,variant,request_json,source_json,result_json,created_at) VALUES (?,?,?,?,?,?,?,?,?,?)",[id,scenario.id,expectedRevision,record.fingerprint,record.engineVersion,record.variant,request,source,result,new Date().toISOString()]);
      return {id,fingerprint:record.fingerprint};
    }));fundingWrites.set(this.db,write);return write;
  }

  async listOverrides(): Promise<PlanOverride[]> {
    const rows = await this.db.select<{
      subject_kind: string; subject_id: string; start_date: string | null; updated_at: string;
    }>("SELECT * FROM plan_overrides");
    return rows.map((r) => ({
      subjectKind: r.subject_kind as PlanOverride["subjectKind"],
      subjectId: r.subject_id,
      startDate: r.start_date,
      updatedAt: r.updated_at,
    }));
  }

  async setOverride(subjectKind: "project" | "scenario", subjectId: string, startDate: string | null): Promise<void> {
    const now = new Date().toISOString();
    if (startDate === null) {
      await this.db.execute("DELETE FROM plan_overrides WHERE subject_kind = ? AND subject_id = ?", [
        subjectKind, subjectId,
      ]);
      return;
    }
    const existing = await this.db.select<{ subject_id: string }>(
      "SELECT subject_id FROM plan_overrides WHERE subject_kind = ? AND subject_id = ?",
      [subjectKind, subjectId],
    );
    if (existing.length === 0) {
      await this.db.execute(
        "INSERT INTO plan_overrides (subject_kind, subject_id, start_date, updated_at) VALUES (?, ?, ?, ?)",
        [subjectKind, subjectId, startDate, now],
      );
    } else {
      await this.db.execute(
        "UPDATE plan_overrides SET start_date = ?, updated_at = ? WHERE subject_kind = ? AND subject_id = ?",
        [startDate, now, subjectKind, subjectId],
      );
    }
  }
}
